import { useState, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tooltip,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Skeleton } from '@material-ui/lab';
import DateFnsUtils from '@date-io/date-fns';
import { format, addDays, differenceInDays, isValid, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';

import clsx from 'clsx';

import { Input } from '#root/components/ui';
import { parseYmd } from '#root/utils';
import {
  DELIVERY_PLACE_MAX,
  EXPIRATION_MARGIN_DAYS,
  MIN_DELIVERY_DAYS,
  getMaxExpirationDays,
} from '../offerValidation';
import { OFFER_EXPIRATION_MAX_DAYS } from '#root/constants/business';
import { QuestionMarkCircleIcon, ArrowDownwardIcon } from '#root/icons';
import styles from './extraData.module.scss';

const expirationOptions = [
  { value: 0, label: 'Hoy', color: '--color-orange' },
  { value: 1, label: 'Mañana', color: '--color-warning' },
  { value: 2, label: 'En 2 días', color: '--color-purple' },
  { value: 3, label: 'En 3 días', color: '--color-purple' },
  { value: 4, label: 'En 4 días', color: '--color-purple' },
];

const getExpirationDaysLabel = (days: number): string => {
  return expirationOptions.find((option) => option.value === days)?.label ?? '';
};

const getExpirationDaysColor = (days: number): string => {
  return expirationOptions.find((option) => option.value === days)?.color ?? '--color-purple';
};

const ExtraData = (props) => {
  const {
    offer,
    flagEnvio,
    handleDateChange,
    handleInputChange,
    loading,
    arriveDate,
    deliveryPlacesError,
    deliveryDateError,
    hasSectionError,
    onDeliveryPlacesBlur,
  } = props;
  const [expanded, setExpanded] = useState(false);
  // Colapsado no se ven los campos, asi que el borde del acordeon hace de aviso.
  const showCollapsedError = Boolean(hasSectionError) && !expanded;

  // Se pasan condicionalmente: `helperText={undefined}` tambien pisa el mensaje
  // interno del picker (minDate, formato), asi que solo se envian si hay error
  // propio. Verificado en @material-ui/pickers/_shared/KeyboardDateInput.js.
  const deliveryDateErrorProps = deliveryDateError
    ? { error: true, helperText: deliveryDateError }
    : {};
  // Tope de vigencia: el menor entre el tope de producto, la fecha de viaje y la
  // fecha de entrega de la oferta. Mismo criterio que el clamp del server.
  const maxExpirationDays = useMemo(
    () => getMaxExpirationDays({ arriveDate, deliveryDate: offer?.delivery_date }),
    [arriveDate, offer?.delivery_date],
  );

  // Si la fecha de viaje ya pasó el drawer no llega a montar este form, pero se
  // deja "Hoy" como piso para no quedarnos sin opciones.
  const availableOptions = useMemo(() => {
    const options = expirationOptions.filter((option) => option.value <= maxExpirationDays);
    return options.length > 0 ? options : [expirationOptions[0]];
  }, [maxExpirationDays]);

  // Derivado de `offer.expiration_date`, no estado local: asi el Select no puede
  // mostrar una cosa mientras se envia otra.
  const expirationDays = useMemo(() => {
    const values = availableOptions.map((option) => option.value);
    const highest = values[values.length - 1];
    const parsed = offer?.expiration_date ? new Date(offer.expiration_date) : null;
    if (!parsed || !isValid(parsed)) return highest;
    const days = differenceInDays(startOfDay(parsed), startOfDay(new Date()));
    const allowed = values.filter((value) => value <= days);
    return allowed.length > 0 ? Math.max(...allowed) : values[0];
  }, [offer?.expiration_date, availableOptions]);

  // La entrega manda solo si es mas ajustada que el tope de producto Y que la
  // fecha de viaje. Ahi conviene explicar por que faltan opciones.
  const deliveryLimitsExpiration = useMemo(() => {
    const delivery = offer?.delivery_date ? new Date(offer.delivery_date) : null;
    if (!delivery || !isValid(delivery)) return false;
    const arrive = parseYmd(arriveDate);
    const today = startOfDay(new Date());
    const daysToDelivery = differenceInDays(startOfDay(delivery), today);
    const daysToArrive = arrive ? differenceInDays(startOfDay(arrive), today) : Infinity;
    return daysToDelivery - EXPIRATION_MARGIN_DAYS < Math.min(OFFER_EXPIRATION_MAX_DAYS, daysToArrive);
  }, [offer?.delivery_date, arriveDate]);

  // Ojo: un `Date` invalido es truthy. Con una fecha a medias (borrar solo el dia)
  // el picker entrega `new Date(NaN)` y `format` lanza RangeError EN RENDER, lo que
  // sin Error Boundary tumba la pantalla entera.
  const deliveryDateValue = offer?.delivery_date ? new Date(offer.delivery_date) : null;
  const dateDelivery =
    deliveryDateValue && isValid(deliveryDateValue)
      ? format(deliveryDateValue, "dd'/'MMM", { locale: es })
      : '---';

  const daysToExpirationDate = (days: number) => {
    return addDays(new Date(), days);
  };

  const handleExpirationChange = (event) => {
    handleDateChange('expiration_date')(daysToExpirationDate(event.target.value));
  };

  const form = (
    <Grid container spacing={3} className={styles.form}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth variant="outlined">
          <InputLabel>Mi oferta vence</InputLabel>
          <Select
            value={expirationDays}
            onChange={handleExpirationChange}
            label="Mi oferta vence">
            {availableOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                style={{ color: `var(${option.color})` }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {deliveryLimitsExpiration && (
            <FormHelperText>
              Debe vencer antes de tu fecha de entrega ({dateDelivery}).
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={es}>
          <KeyboardDatePicker
            disableToolbar
            fullWidth
            label="Fecha de entrega"
            inputVariant="outlined"
            format="dd/MM/yyyy"
            value={offer.delivery_date}
            onChange={handleDateChange('delivery_date')}
            minDate={addDays(startOfDay(new Date()), MIN_DELIVERY_DAYS)}
            InputAdornmentProps={{ position: 'end' }}
            minDateMessage="La entrega debe ser al menos mañana"
            invalidDateMessage="Formato de fecha no válido"
            maxDateMessage="La fecha es demasiado lejana"
            {...deliveryDateErrorProps}
          />
        </MuiPickersUtilsProvider>
      </Grid>
      <Grid item xs={12}>
        {loading ? (
          <Skeleton variant="rect" width="100%" height={56} />
        ) : (
          <Input
            id="delivery_places"
            disabled={flagEnvio}
            label="Lugares de entrega"
            value={offer.delivery_places}
            onChange={handleInputChange}
            onBlur={onDeliveryPlacesBlur}
            error={Boolean(deliveryPlacesError)}
            helperText={deliveryPlacesError}
            maxLength={DELIVERY_PLACE_MAX}
          />
        )}
        {flagEnvio && (
          <Tooltip
            arrow
            title="Guvery le enviará este pedido al comprador, porque se encuentra fuera de Lima. "
            placement="bottom">
            <div className={styles.formHelp}>
              <span>
                Debe entregarse en la oficina de MailBoxes (Miraflores, San Isidro ó Surco).
              </span>
              <QuestionMarkCircleIcon />
            </div>
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );

  const Text = (
    <div className={styles.header}>
      {expanded ? (
        <div>
          <strong>Datos de entrega</strong>
        </div>
      ) : (
        <div>
          Entregando el <strong>{dateDelivery}</strong> en [{' '}
          <strong>{offer.delivery_places}</strong> ]. Mi oferta vence{' '}
          <strong style={{ color: `var(${getExpirationDaysColor(expirationDays)})` }}>
            {getExpirationDaysLabel(expirationDays)}
          </strong>
          .
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.root}>
      <Accordion
        className={clsx(styles.acordion, showCollapsedError && styles.acordionError)}
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}>
        <AccordionSummary
          aria-controls="panel1a-content"
          id="panel1a-header"
          expandIcon={<ArrowDownwardIcon />}
          IconButtonProps={{ size: 'small' }}>
          {Text}
        </AccordionSummary>
        <AccordionDetails>{form}</AccordionDetails>
      </Accordion>
    </div>
  );
};

export default ExtraData;
